// src/components/roadmap/RoadmapBoard.jsx
import React from "react";
import { Link } from "react-router-dom";
import { entityPath } from "../../lib/productInsights";
import { Card } from "components/ui/card";
import { Badge } from "components/ui/badge";
import { Progress } from "components/ui/progress";
import { Button } from "components/ui/button";
import { ChevronLeft, ChevronRight, CheckCircle2, User, Pencil, Target } from "lucide-react";
import SampleBadge from "../guide/SampleBadge";

const STATUS_TONE = { draft: "default", planned: "accent", in_progress: "warning", done: "success" };
const HORIZONS = ["Now", "Next", "Later"];

function Column({ title, items, problems, okrs, onMove, onUpdate, onEdit, maxHeight }) {
  // Check if item was recently imported (within last 5 minutes)
  const isRecentlyImported = (item) => {
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    const updatedAt = new Date(item.updated_at);
    return updatedAt > fiveMinutesAgo && item.tags?.includes('jira-import');
  };

  return (
    <section className="board-lane lane-scroll" aria-label={`${title} horizon`} style={maxHeight ? { maxHeight } : undefined}>
      <header className="board-lane-header">
        <h2>{title}</h2>
        <span className="tabular">{items.length}</span>
      </header>

      {items.length === 0 ? <div className="board-lane-empty">No initiatives</div> : <div className="board-lane-body" tabIndex={0} aria-label={`${title} initiatives`}>
      {items.map((it, idx) => {
        const index = HORIZONS.indexOf(it.time_horizon);
        const linkedOKR = it.okr_ref ? okrs.find(okr => String(okr.id) === String(it.okr_ref)) : null;
        const progress = Number(it.progress) || 0;
        return (
          <Card key={it.id} className="interactive" style={{ padding: 12, display: "grid", gap: 10, ...(isRecentlyImported(it) ? { borderColor: "var(--success-border)" } : null) }}>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              <Badge variant={STATUS_TONE[it.status] || "default"} className="badge-dot" style={{ textTransform: "capitalize" }}>
                {String(it.status || "draft").replace(/_/g, " ")}
              </Badge>
              {isRecentlyImported(it) && <Badge variant="success">New</Badge>}
              <span className="tabular" style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)" }} title="Impact">
                Impact {Number(it.impact) || 0}/5
              </span>
            </div>

            <div style={{ minWidth: 0 }}>
              <Link className="card-link" to={entityPath("initiatives", it.id)} title={it.title}>{it.title}</Link>
              <SampleBadge id={it.id}/>
              <p style={{ fontSize: 12, margin: "4px 0 0", color: "var(--fg-muted)", lineHeight: 1.45 }}>
                {it.expected_outcome || it.expected_impact || "Expected outcome not recorded"}
              </p>
              {it.ai_priority && <div style={{ marginTop: 8 }}><Badge variant="accent">AI priority {it.priority_score}/100</Badge><p className="v2-clamp-2" title={it.ai_priority.reason} style={{ fontSize: 12, color: 'var(--fg-muted)', margin: '6px 0' }}>{it.ai_priority.reason}</p></div>}
              {it.automation && <div><Badge variant="accent">Autopilot draft</Badge><p className="v2-clamp-2" title={it.automation.reason} style={{ fontSize: 12, color: 'var(--fg-muted)', margin: '6px 0' }}>{it.automation.reason}</p><Link className="v2-text-action" to="/autopilot">Review agent activity & evidence</Link></div>}
              {it.source_metadata?.key && <a className="v2-text-action" href={it.source_metadata.url} target="_blank" rel="noreferrer">{it.source_metadata.key}{it.source_metadata.parent_key ? ` · Parent ${it.source_metadata.parent_key}` : ''}</a>}
            </div>

            <div style={{ display: "grid", gap: 4, fontSize: 12 }}>
              {(it.problem_card_ids || []).map(id => (
                <Link key={id} className="v2-text-action" style={{ fontSize: 12 }} to={entityPath("problems", id)}>
                  {problems.find(p => p.id === id)?.title || "Review linked problem"}
                </Link>
              ))}
              {it.problem_id && !(it.problem_card_ids || []).length && problems?.length > 0 && (
                <span style={{ color: "var(--fg-secondary)" }}>
                  {(problems.find((p) => String(p.id) === String(it.problem_id)) || {}).title || "Linked problem"}
                </span>
              )}
            </div>

            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {it.okr_ref && (
                <Badge variant="accent" title="Linked objective"><Target size={11} />OKR: {linkedOKR ? linkedOKR.title : it.okr_ref}</Badge>
              )}
              <Badge variant={it.success_metric ? "default" : "warning"}>{it.success_metric ? `Metric: ${it.success_metric}` : "No success metric"}</Badge>
              {Array.isArray(it.tags) && it.tags.slice(0, 3).map((tag) => <Badge key={`${it.id}-${tag}`}>{tag}</Badge>)}
              {Array.isArray(it.tags) && it.tags.length > 3 && <Badge>+{it.tags.length - 3}</Badge>}
            </div>

            <div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 4, color: "var(--fg-muted)" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  {it.owner ? <><User size={12} /> {it.owner}</> : "Delivery"}
                </span>
                <span className="tabular" style={{ fontWeight: 600, color: "var(--fg-secondary)" }}>{progress}%</span>
              </div>
              <Progress value={progress} style={{ height: 4 }} />
            </div>

            <div style={{ display: "flex", gap: 4, alignItems: "center", borderTop: "1px solid var(--border)", paddingTop: 8 }}>
              <Button variant="ghost" size="sm" className="btn-icon" onClick={() => onEdit?.(it)} title="Edit initiative" aria-label="Edit initiative" data-guide={idx === 0 ? "initiative-okr" : undefined}>
                <Pencil size={14} />
              </Button>
              {it.status !== "done" && (
                <Button variant="ghost" size="sm" onClick={() => onUpdate?.(it.id, { status: "done" })} title="Mark done">
                  <CheckCircle2 size={14} />
                  Done
                </Button>
              )}
              <div style={{ marginLeft: "auto", display: "flex", gap: 2 }}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="btn-icon"
                  disabled={index <= 0}
                  onClick={() => onMove?.(it.id, it.time_horizon === "Later" ? "Next" : "Now")}
                  title={index > 0 ? `Move to ${HORIZONS[index - 1]}` : "Already in Now"}
                  aria-label="Move to earlier horizon"
                >
                  <ChevronLeft size={15} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="btn-icon"
                  disabled={index >= 2}
                  onClick={() => onMove?.(it.id, it.time_horizon === "Now" ? "Next" : "Later")}
                  title={index < 2 ? `Move to ${HORIZONS[index + 1]}` : "Already in Later"}
                  aria-label="Move to later horizon"
                >
                  <ChevronRight size={15} />
                </Button>
              </div>
            </div>
          </Card>
        );
      })}
      </div>}
    </section>
  );
}

export default function RoadmapBoard({ items = [], problems = [], okrs = [], isLoading, onMove, onUpdate, onEdit, laneMaxHeight }) {
  // Reviewed AI priority orders each horizon; recency breaks ties.
  const sortByUpdated = (a, b) => (Number(b.priority_score) || 0) - (Number(a.priority_score) || 0) || new Date(b.updated_at || 0) - new Date(a.updated_at || 0);

  const now = items.filter((i) => i.time_horizon === "Now").sort(sortByUpdated);
  const next = items.filter((i) => i.time_horizon === "Next").sort(sortByUpdated);
  const later = items.filter((i) => i.time_horizon === "Later").sort(sortByUpdated);

  if (isLoading) {
    return <div className="grid cols-3">{HORIZONS.map(h => <div key={h} className="skeleton" style={{ height: 220 }} />)}</div>;
  }

  return (
    <div className="grid cols-3" style={{ alignItems: "start" }}>
      <Column title="Now" items={now} problems={problems} okrs={okrs} onMove={onMove} onUpdate={onUpdate} onEdit={onEdit} maxHeight={laneMaxHeight} />
      <Column title="Next" items={next} problems={problems} okrs={okrs} onMove={onMove} onUpdate={onUpdate} onEdit={onEdit} maxHeight={laneMaxHeight} />
      <Column title="Later" items={later} problems={problems} okrs={okrs} onMove={onMove} onUpdate={onUpdate} onEdit={onEdit} maxHeight={laneMaxHeight} />
    </div>
  );
}
